import { RESET_STATE } from 'store/global/actions';

import { PRICE_SOURCES_ACTION_TYPE } from '../actions';

const IDLE_REPUBLISH = {
  isPublishing: false,
  errorMessage: null,
  isConflict: false,
  isForbidden: false,
  conflictCode: null,
  result: null,
};

const detailInitialState = {
  current: { data: null, isLoading: false, hasError: false },
  versions: { data: [], isLoading: false, hasError: false },
  baseVersion: { data: null, isLoading: false, hasError: false, isLoaded: false },
  servingVersion: { data: null },
  versionSave: { isSaving: false, errorMessage: null, isConflict: false, result: null },
  republish: IDLE_REPUBLISH,
};

const initialState = {
  list: { data: [], isLoading: false, hasError: false },
  save: { isSaving: false, errorMessage: null },
  ...detailInitialState,
};

const upsertById = (items, item) =>
  items.some(entry => entry.priceSourceId === item.priceSourceId)
    ? items.map(entry => (entry.priceSourceId === item.priceSourceId ? item : entry))
    : [...items, item];

const priceSourcesReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case PRICE_SOURCES_ACTION_TYPE.SET_LIST_LOADING:
      return {
        ...state,
        list: {
          ...state.list,
          isLoading: payload,
          hasError: payload ? false : state.list.hasError,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.GET_LIST:
      return { ...state, list: { data: payload ?? [], isLoading: false, hasError: false } };

    case PRICE_SOURCES_ACTION_TYPE.SET_LIST_ERROR:
      return { ...state, list: { ...state.list, isLoading: false, hasError: true } };

    case PRICE_SOURCES_ACTION_TYPE.SET_CURRENT_LOADING:
      return {
        ...state,
        current: {
          ...state.current,
          isLoading: payload,
          hasError: payload ? false : state.current.hasError,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.GET_CURRENT:
      return { ...state, current: { data: payload ?? null, isLoading: false, hasError: false } };

    case PRICE_SOURCES_ACTION_TYPE.SET_CURRENT_ERROR:
      return { ...state, current: { ...state.current, isLoading: false, hasError: true } };

    case PRICE_SOURCES_ACTION_TYPE.SET_VERSIONS_LOADING:
      return {
        ...state,
        versions: {
          ...state.versions,
          isLoading: payload,
          hasError: payload ? false : state.versions.hasError,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.GET_VERSIONS:
      return { ...state, versions: { data: payload ?? [], isLoading: false, hasError: false } };

    case PRICE_SOURCES_ACTION_TYPE.SET_VERSIONS_ERROR:
      return { ...state, versions: { ...state.versions, isLoading: false, hasError: true } };

    case PRICE_SOURCES_ACTION_TYPE.SET_BASE_VERSION_LOADING:
      return {
        ...state,
        baseVersion: {
          ...state.baseVersion,
          isLoading: payload,
          hasError: payload ? false : state.baseVersion.hasError,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.GET_BASE_VERSION:
      return {
        ...state,
        baseVersion: { data: payload ?? null, isLoading: false, hasError: false, isLoaded: true },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_BASE_VERSION_ERROR:
      return {
        ...state,
        baseVersion: { ...state.baseVersion, isLoading: false, hasError: true },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_SERVING_VERSION:
      return { ...state, servingVersion: { data: payload ?? null } };

    case PRICE_SOURCES_ACTION_TYPE.SET_SAVING:
      return { ...state, save: { isSaving: payload, errorMessage: null } };

    case PRICE_SOURCES_ACTION_TYPE.SET_SAVE_ERROR:
      return { ...state, save: { isSaving: false, errorMessage: payload ?? null } };

    case PRICE_SOURCES_ACTION_TYPE.CLEAR_SAVE_ERROR:
      return { ...state, save: { isSaving: false, errorMessage: null } };

    case PRICE_SOURCES_ACTION_TYPE.UPSERT_SOURCE:
      return {
        ...state,
        list: { ...state.list, data: upsertById(state.list.data, payload) },
        current: {
          ...state.current,
          data:
            state.current.data?.priceSourceId === payload.priceSourceId
              ? payload
              : state.current.data,
        },
        save: { isSaving: false, errorMessage: null },
      };

    case PRICE_SOURCES_ACTION_TYPE.REMOVE_SOURCE:
      return {
        ...state,
        list: {
          ...state.list,
          data: state.list.data.filter(entry => entry.priceSourceId !== payload),
        },
        save: { isSaving: false, errorMessage: null },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVING:
      return {
        ...state,
        versionSave: { isSaving: payload, errorMessage: null, isConflict: false, result: null },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVE_ERROR:
      return {
        ...state,
        versionSave: {
          isSaving: false,
          errorMessage: payload?.message ?? null,
          isConflict: !!payload?.isConflict,
          result: null,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_VERSION_SAVE_RESULT:
      return {
        ...state,
        versionSave: { isSaving: false, errorMessage: null, isConflict: false, result: payload },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISHING:
      return { ...state, republish: { ...IDLE_REPUBLISH, isPublishing: true } };

    case PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISH_ERROR:
      return {
        ...state,
        republish: {
          ...IDLE_REPUBLISH,
          errorMessage: payload?.message ?? null,
          isConflict: !!payload?.isConflict,
          isForbidden: !!payload?.isForbidden,
          conflictCode: payload?.conflictCode ?? null,
        },
      };

    case PRICE_SOURCES_ACTION_TYPE.SET_REPUBLISH_RESULT:
      return { ...state, republish: { ...IDLE_REPUBLISH, result: payload ?? null } };

    case PRICE_SOURCES_ACTION_TYPE.CLEAR_REPUBLISH:
      return { ...state, republish: IDLE_REPUBLISH };

    case PRICE_SOURCES_ACTION_TYPE.CLEAR_SOURCE_DETAIL:
      return { ...state, ...detailInitialState };

    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default priceSourcesReducer;
